import { Partners, SuperadminPartners } from "./pages/Partners";
import { SuperadminFinance } from "./pages/SuperadminFinance";
import { SuperadminTaxes } from "./pages/SuperadminTaxes";
import { PaymentResult } from "./pages/PaymentResult";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { lazy } from "react";

import { AppShell } from "./components/AppShell";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AuthProvider } from "./hooks/useAuth";
import { Login } from "./pages/Login";

const Chat = lazy(() => import("./pages/Chat").then((module) => ({ default: module.Chat })));
const History = lazy(() =>
  import("./pages/History").then((module) => ({ default: module.History }))
);
const Gallery = lazy(() =>
  import("./pages/Gallery").then((module) => ({ default: module.Gallery }))
);
const Memory = lazy(() =>
  import("./pages/Memory").then((module) => ({ default: module.Memory }))
);
const Reminders = lazy(() =>
  import("./pages/Reminders").then((module) => ({ default: module.Reminders }))
);
const Settings = lazy(() =>
  import("./pages/Settings").then((module) => ({ default: module.Settings }))
);
const PublicCharacters = lazy(() =>
  import("./pages/PublicCharacters").then((module) => ({ default: module.PublicCharacters }))
);
const AiSettings = lazy(() =>
  import("./pages/AiSettings").then((module) => ({ default: module.AiSettings }))
);
const SuperadminOverview = lazy(() =>
  import("./pages/SuperadminOverview").then((module) => ({
    default: module.SuperadminOverview,
  }))
);
const SuperadminBroadcasts = lazy(() =>
  import("./pages/SuperadminBroadcasts").then((module) => ({
    default: module.SuperadminBroadcasts,
  }))
);
const SuperadminUserDetail = lazy(() =>
  import("./pages/SuperadminUserDetail").then((module) => ({
    default: module.SuperadminUserDetail,
  }))
);
const SuperadminRegistrations = lazy(() =>
  import("./pages/SuperadminRegistrations").then((module) => ({ default: module.SuperadminRegistrations }))
);
const SuperadminUsers = lazy(() =>
  import("./pages/SuperadminUsers").then((module) => ({ default: module.SuperadminUsers }))
);
const SuperadminModels = lazy(() =>
  import("./pages/SuperadminModels").then((module) => ({ default: module.SuperadminModels }))
);
const SuperadminInvites = lazy(() =>
  import("./pages/SuperadminInvites").then((module) => ({ default: module.SuperadminInvites }))
);
const SuperadminMemoryQuality = lazy(() =>
  import("./pages/SuperadminMemoryQuality").then((module) => ({
    default: module.SuperadminMemoryQuality,
  }))
);
const Telegram = lazy(() =>
  import("./pages/Telegram").then((module) => ({ default: module.Telegram }))
);
const Usage = lazy(() =>
  import("./pages/Usage").then((module) => ({ default: module.Usage }))
);
const Pricing = lazy(() =>
  import("./pages/Pricing").then((module) => ({ default: module.Pricing }))
);

/**
 * Router root. `basename` matches api-gateway's ServeDir mount (/admin).
 * Structure:
 *   /login            — public
 *   /*                — protected, rendered inside AppShell
 */
export function App() {
  return (
    <BrowserRouter
      basename="/cabinet/"
      future={{ v7_relativeSplatPath: true, v7_startTransition: true }}
    >
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route element={<ProtectedRoute />}>
            <Route element={<AppShell />}>
              <Route index element={<Chat />} />
              <Route path="/memory" element={<Memory />} />
              <Route path="/history" element={<History />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/reminders" element={<Reminders />} />
              <Route path="/telegram" element={<Telegram />} />
              <Route path="/usage" element={<Usage />} />
              <Route path="/partners" element={<Partners />} />
              <Route path="/admin-panel/partners" element={<SuperadminPartners />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/character" element={<Settings />} />
              <Route path="/characters" element={<PublicCharacters />} />
              <Route path="/settings" element={<AiSettings />} />
              {/* Superadmin panel. Role gate is also enforced server-side;
                  the UI just hides the sidebar link when role != superadmin. */}
              <Route path="/payment/:id" element={<PaymentResult />} />
              <Route path="/admin-panel/finance" element={<SuperadminFinance />} />
              <Route path="/admin-panel/taxes" element={<SuperadminTaxes />} />
              <Route path="/admin-panel" element={<SuperadminOverview />} />
              <Route path="/admin-panel/registrations" element={<SuperadminRegistrations />} />
              <Route path="/admin-panel/users" element={<SuperadminUsers />} />
              <Route path="/admin-panel/users/:id" element={<SuperadminUserDetail />} />
              <Route path="/admin-panel/broadcasts" element={<SuperadminBroadcasts />} />
              <Route path="/admin-panel/models" element={<SuperadminModels />} />
              <Route path="/admin-panel/invites" element={<SuperadminInvites />} />
              <Route path="/admin-panel/memory-quality" element={<SuperadminMemoryQuality />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
